/**
 * =============================================================================
 * IDS CRM Web - Settings -> Notifications
 * =============================================================================
 *
 * Gareth, 14 October 2026: "all notification can be controlled from the
 * notifications page. This page should then be available to everyone to set
 * up against their own email or teams."
 *
 *  - My notifications (everyone): each kind with an Email and a Teams tick,
 *    and whether daily reminders arrive as one morning summary. The bell
 *    always gets every notification.
 *  - Company settings (admins): the email switch for the whole CRM, the
 *    morning summary for people who have not chosen, and one table holding
 *    the starting Email and Teams ticks and what is posted to the Teams
 *    channels. This page is the one place notifications are set (Gareth,
 *    14 October: "only one place where the notifications can be set").
 *  - The Teams connection (admins), moved here from Integrations: only the
 *    connection itself, no ticks.
 *  - The email log (managers and admins), as before.
 *
 * The server decides what is sent (api services/notificationDelivery.js);
 * this page only saves the choices.
 * =============================================================================
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import NotificationTicks from '../components/NotificationTicks';
import TeamsSettings from './TeamsSettings';
import NotificationLogSettings from './NotificationLogSettings';

export const MINE_READ_FAILED = 'Your notification settings could not be read. Check your signal and reload the page.';
export const MINE_SAVED = 'Saved. Your notifications follow these ticks from now on.';
export const MINE_RESET = 'Back to the company\'s starting ticks.';
export const COMPANY_READ_FAILED = 'The company notification settings could not be read. Reload the page.';
export const COMPANY_SAVED = 'Company notification settings saved.';

async function call(authFetch, path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await authFetch(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch (err) {
    throw new Error('That could not be sent. Check your signal and try again.');
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'That could not be saved. Try again.');
  return data;
}

/** Everyone: which notifications reach me by email and by Teams. */
export function MyNotifications() {
  const { authFetch } = useAuth();
  const { guardElement, markDirty, resetDirty } = useUnsavedChanges();
  const [data, setData] = useState(null);
  const [ticks, setTicks] = useState({});
  const [summary, setSummary] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const show = useCallback((response) => {
    setData(response);
    setTicks(response.mine.ticks);
    setSummary(response.mine.summary);
    setDirty(false);
    resetDirty();
  }, [resetDirty]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await call(authFetch, '/api/v1/notifications/settings');
        if (!cancelled) show(response);
      } catch (err) {
        if (!cancelled) setError(MINE_READ_FAILED);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, show]);

  const change = (kind, channel, value) => {
    setTicks(previous => ({ ...previous, [kind]: { ...previous[kind], [channel]: value } }));
    setDirty(true);
    setMessage(null);
    markDirty();
  };

  const save = async (body, doneMessage) => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      show(await call(authFetch, '/api/v1/notifications/settings', { method: 'PUT', body }));
      setMessage(doneMessage);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="settings-section notification-settings">
        <h2>My notifications</h2>
        {error ? <div className="admin-error" role="alert">{error}</div> : <p className="settings-hint">Loading your notification settings...</p>}
      </div>
    );
  }

  const { company } = data;
  const emailOff = !company.email_enabled;
  const teamsOff = !company.teams_ready;

  return (
    <div className="settings-section notification-settings">
      <h2>My notifications</h2>
      <p className="settings-hint">
        The bell at the top of the page always shows every notification. Tick which ones should also reach you by
        email{data.address ? ` (to ${data.address})` : ''} and as a Teams message to the account you sign in with.
      </p>
      {emailOff && (
        <p className="notification-note" role="status">Email is switched off for the whole company, so no notification is emailed.</p>
      )}
      {!emailOff && !company.email_live && (
        <p className="notification-note" role="status">Email is not connected on the server yet, so emails are recorded but not delivered.</p>
      )}
      {teamsOff && (
        <p className="notification-note" role="status">Teams messages are not set up for the company yet, so nothing is sent to Teams.</p>
      )}

      <label className="notification-summary-switch">
        <input
          type="checkbox"
          checked={summary}
          disabled={saving}
          onChange={(event) => { setSummary(event.target.checked); setDirty(true); setMessage(null); markDirty(); }}
        />
        <span>Send my daily reminders as one morning summary, instead of one message for each</span>
      </label>

      <NotificationTicks
        idPrefix="mine"
        groups={data.groups}
        kinds={data.kinds}
        ticks={ticks}
        onChange={change}
        emailOff={emailOff}
        teamsOff={teamsOff}
        disabled={saving}
      />

      {error && <div className="admin-error" role="alert">{error}</div>}
      {message && <div className="admin-info" role="status">{message}</div>}
      <div className="notification-actions">
        <button type="button" className="btn-primary" disabled={saving} onClick={() => save({ kinds: ticks, summary }, MINE_SAVED)}>
          {saving ? 'Saving...' : 'Save my notifications'}
        </button>
        {data.mine.customised && (
          <button type="button" className="btn-secondary" disabled={saving} onClick={() => save({ reset: true }, MINE_RESET)}>
            Use the company&apos;s starting ticks
          </button>
        )}
        {dirty && <span className="notification-unsaved">Unsaved changes: nothing changes until you save.</span>}
      </div>
      {guardElement}
    </div>
  );
}

/** Admins: the company's email switch, the summary default and the starting ticks. */
export function CompanyNotifications() {
  const { authFetch } = useAuth();
  const { guardElement, markDirty, resetDirty } = useUnsavedChanges();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const show = useCallback((response) => {
    setData(response);
    setForm({
      email_enabled: response.email_enabled,
      summary_default: response.summary_default,
      starting: response.starting,
      channel: response.channel || {}
    });
    setDirty(false);
    resetDirty();
  }, [resetDirty]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await call(authFetch, '/api/v1/notifications/company');
        if (!cancelled) show(response);
      } catch (err) {
        if (!cancelled) setError(COMPANY_READ_FAILED);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, show]);

  const edit = (change) => {
    setForm(previous => ({ ...previous, ...change(previous) }));
    setDirty(true);
    setMessage(null);
    markDirty();
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      show(await call(authFetch, '/api/v1/notifications/company', { method: 'PUT', body: form }));
      setMessage(COMPANY_SAVED);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data || !form) {
    return (
      <div className="settings-section notification-settings">
        <h2>Company settings</h2>
        {error ? <div className="admin-error" role="alert">{error}</div> : <p className="settings-hint">Loading the company settings...</p>}
      </div>
    );
  }

  return (
    <div className="settings-section notification-settings">
      <h2>Company settings</h2>
      <p className="settings-hint">Admins only. These apply to everyone.</p>

      <label className="notification-summary-switch">
        <input
          type="checkbox"
          checked={form.email_enabled}
          disabled={saving}
          onChange={event => edit(() => ({ email_enabled: event.target.checked }))}
        />
        <span>Send notification emails from the CRM. Off stops every notification email, whatever people have ticked.</span>
      </label>
      <label className="notification-summary-switch">
        <input
          type="checkbox"
          checked={form.summary_default}
          disabled={saving}
          onChange={event => edit(() => ({ summary_default: event.target.checked }))}
        />
        <span>Daily reminders arrive as one morning summary for people who have not chosen</span>
      </label>

      <h3>Starting ticks and Teams channel posts</h3>
      <p className="settings-hint">
        Email and Teams: what each person gets until they choose their own (people who have saved their own
        choices keep them). Teams channel: what is posted into the shared Teams channels connected below, for
        everyone in the channel to see.
      </p>
      {!data.teams_channels_ready && (
        <p className="notification-note" role="status">No Teams channel is connected yet, so nothing is posted to a channel.</p>
      )}
      <NotificationTicks
        idPrefix="starting"
        groups={data.groups}
        kinds={data.kinds}
        columns={[
          { key: 'email', label: 'Email' },
          { key: 'teams', label: 'Teams' },
          { key: 'channel', label: 'Teams channel' }
        ]}
        ticks={Object.fromEntries(data.kinds.map(kind => [kind.key, {
          ...form.starting[kind.key],
          channel: Boolean(form.channel[kind.key])
        }]))}
        onChange={(kind, column, value) => edit(previous => (column === 'channel'
          ? { channel: { ...previous.channel, [kind]: value } }
          : { starting: { ...previous.starting, [kind]: { ...previous.starting[kind], [column]: value } } }))}
        disabled={saving}
      />

      {error && <div className="admin-error" role="alert">{error}</div>}
      {message && <div className="admin-info" role="status">{message}</div>}
      <div className="notification-actions">
        <button type="button" className="btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving...' : 'Save company settings'}
        </button>
        {dirty && <span className="notification-unsaved">Unsaved changes: nothing changes until you save.</span>}
      </div>
      {guardElement}
    </div>
  );
}

function NotificationSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const seesEmailLog = ['admin', 'manager'].includes(user?.role);
  return (
    <>
      <MyNotifications />
      {isAdmin && <CompanyNotifications />}
      {isAdmin && <TeamsSettings />}
      {seesEmailLog && <NotificationLogSettings />}
    </>
  );
}

export default NotificationSettings;
